import { Link } from 'react-router-dom';

import { DocumentFiltersPopup } from './doc-filters';
import { DocSortPopup } from './doc-sort';
import { FilterPills } from '../../../../../../../components/filter-pills';
import { FilterPopoverContent } from '../../../../../../../components/filter-popover-content';
import { Icon } from '../../../../../../../components/icon';
import { NaturalLanguageSearchBox } from '../../../../../../../components/natural-language-search-bar';
import { PageContainer } from '../../../../../../../components/page-container';
import { PageHeader } from '../../../../../../../components/page-header';
import { PageTitle } from '../../../../../../../components/page-title';
import { Popover, PopoverTrigger } from '../../../../../../../components/shadcn/popover';
import { SubsetChip } from '../../../../../../../components/subset-chip';
import { useMatterProcessingStore } from '../../../../../../../stores/matter-processing-store';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Pagination } from '@/new/components/pagination';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { DocFilters } from '@/types';
import { FilterPill } from '@/utils/buildFilterPills';

interface DocumentEditorToolbarProps {
  matterId: string;
  subsetId: string;
  isAllDocuments?: boolean;
  openDownloadModal: () => void;
  openDownloadZipModal: () => void;
  openDownloadAllZipModal: () => void;
  globalFilter: string;
  setGlobalFilter: (value: string) => void;
  onSearchCall: (value: string, useNaturalLanguage: boolean) => void;
  clearSearch: () => void;
  processingNL?: boolean;
  currentPage: number;
  totalPages?: number;
  prevPage: () => void;
  nextPage: () => void;
  goToPage: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  hasActiveFilters: boolean;
  totalCount?: number;
  totalUnfilteredCount?: number;
  totalProcessedCount?: number;
  filterPills: FilterPill[];
  onClearAllFilters: () => void;
  filters: DocFilters;
  setFilters: (filters: DocFilters) => void;
  allDocumentTypes: string[];
  docThemes: string[];
  sortBy: string;
  setSortBy: (value: string) => void;
  sortOrder: 'ASC' | 'DESC';
  setSortOrder: (value: 'ASC' | 'DESC') => void;
}

export function DocTableHeader({
  matterId,
  subsetId,
  isAllDocuments,
  openDownloadModal,
  openDownloadZipModal,
  openDownloadAllZipModal,
  globalFilter,
  setGlobalFilter,
  onSearchCall,
  clearSearch,
  currentPage,
  totalPages,
  prevPage,
  nextPage,
  goToPage,
  pageSize,
  onPageSizeChange,
  hasActiveFilters,
  processingNL,
  totalCount,
  totalUnfilteredCount,
  filterPills,
  onClearAllFilters,
  filters,
  setFilters,
  allDocumentTypes,
  docThemes,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
}: DocumentEditorToolbarProps) {
  const { getProcessingState } = useMatterProcessingStore();

  return (
    <PageContainer className="border-b pb-3">
      <PageHeader className="my-3.25">
        <div className="flex items-center gap-4">
          <PageTitle text="Documents" />
          <SubsetChip subsetId={subsetId} className="mt-0.5" />
        </div>

        <div className="flex flex-col gap-2.5">
          <div className="flex justify-end gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button disabled={!getProcessingState(matterId)?.hasCompletedRuns}>
                  <span className="iconify lucide--download" />
                  <span className="font-medium">Download</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {hasActiveFilters ? (
                  <>
                    <DropdownMenuItem onClick={openDownloadZipModal}>Download Filtered Zip</DropdownMenuItem>
                    <DropdownMenuItem onClick={openDownloadAllZipModal}>Download All Zip</DropdownMenuItem>
                    <DropdownMenuItem onClick={openDownloadModal}>Download List</DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem onClick={openDownloadModal}>Download List</DropdownMenuItem>
                    <DropdownMenuItem onClick={openDownloadAllZipModal}>Download All Zip</DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {isAllDocuments !== undefined &&
              (isAllDocuments ? (
                <Button asChild variant="primary">
                  <Link to={`/matters/${matterId}/docs/upload`}>
                    <Icon className="lucide--upload" />
                    Upload Documents
                  </Link>
                </Button>
              ) : (
                <Button asChild variant="primary">
                  <Link
                    to={`/matters/${matterId}/subsets/${subsetId}/edit`}
                    onClick={() => {
                      trackCustomEvent(TrackingEventsV2.ClickAddDocuments);
                    }}
                  >
                    <Icon className="lucide--diff" />
                    Edit Documents in Subset
                  </Link>
                </Button>
              ))}
          </div>

          <Pagination
            canGetPrevPage={currentPage > 0}
            canGetNextPage={totalPages !== undefined && currentPage < totalPages - 1}
            prevPage={prevPage}
            nextPage={nextPage}
            currentPage={currentPage}
            noOfPages={totalPages}
            goToPage={goToPage}
            onPageSizeChange={onPageSizeChange}
            pageSize={pageSize}
            totalCount={totalUnfilteredCount}
            filteredCount={totalCount}
            entityName="documents"
          />
        </div>
      </PageHeader>

      <div className="flex gap-2">
        <NaturalLanguageSearchBox
          value={globalFilter}
          onChange={setGlobalFilter}
          onSearchCall={onSearchCall}
          clearSearch={clearSearch}
          processingNL={processingNL}
          entityType="documents"
        />

        <Popover>
          <PopoverTrigger asChild>
            <Button>
              <Icon className="lucide--sort-asc" />
              <span>Sort</span>
            </Button>
          </PopoverTrigger>
          <FilterPopoverContent
            clearAllFilters={() => {
              setSortBy('');
              setSortOrder('DESC');
            }}
          >
            <DocSortPopup
              activeSortBy={sortBy}
              activeSortOrder={sortOrder}
              setSortBy={setSortBy}
              setSortOrder={setSortOrder}
              hasActiveSearch={!!globalFilter && globalFilter.trim() !== ''}
            />
          </FilterPopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button>
              <Icon className="lucide--funnel-plus" />
              <span>Add Filters</span>
            </Button>
          </PopoverTrigger>
          <FilterPopoverContent clearAllFilters={onClearAllFilters}>
            <DocumentFiltersPopup
              matterId={matterId}
              activeFilters={filters}
              setFilters={setFilters}
              allDocumentTypes={allDocumentTypes}
              docThemes={docThemes}
            />
          </FilterPopoverContent>
        </Popover>
      </div>

      <div className="mt-2.5">
        <FilterPills pills={filterPills} onClearAll={onClearAllFilters} />
      </div>
    </PageContainer>
  );
}
